import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { actions, FullTask, isOwner, taskStatusesConfig } from 'entities';

import { useOwnershipChange } from 'providers/OwnershipChange';
import { useUser } from 'hooks';

import { useTaskModal } from '../../context.taskModal';

interface ChangeTaskOwnerHook {
  handleChangeClick: () => void;
  canChangeOwner: boolean;
}

export function useSubTaskOwnerChange(subTask: FullTask['subTasks'][number]): ChangeTaskOwnerHook {
  const { t } = useLang();
  const loggedUser = useUser();

  const { updateTask, task } = useTaskModal();

  const { openChangeOwnerModal } = useOwnershipChange();

  const changeOwner = actions.projects.changeSubTaskOwner.use();

  const handleChangeClick = useCallback(() => {
    openChangeOwnerModal({
      text: `${t('change-task-owner')}?`,

      currentOwner: subTask.owner,

      allowRemoval: false,

      onConfirm: async (owner, justification) => {
        const success = await changeOwner.execute({
          project: task.projectId,
          task: task.id,
          subTask: subTask.id,
          owner: owner!.id,
          justification,
        });

        if (!success) return;

        updateTask((old) => ({
          ...old,

          subTasks: old.subTasks.map((sub) =>
            sub.id === subTask.id ? { ...sub, owner: owner! } : sub,
          ),
        }));
      },
    });
  }, [changeOwner, subTask, openChangeOwnerModal, t, task, updateTask]);

  const canChangeOwner = [
    subTask.owner.id === loggedUser.id || isOwner(task, loggedUser),
    subTask.status !== taskStatusesConfig.done.code,
  ].every(Boolean);

  return {
    handleChangeClick,
    canChangeOwner,
  };
}
